import React, { useState, Fragment } from 'react';
import { Row, Col, Card as BSCard, Button, Container, Modal, ModalHeader, ModalBody } from 'reactstrap';

import LinkOut from "../base/components/LinkOut";
import PropControl from "../base/components/PropControl";
import { BannerControls, OnXControls } from './CommonControls';

import { Card } from '../base/components/CardAccordion';
import Misc from '../base/components/Misc';
import { getType } from '../base/data/DataClass';
// Default string values from adunit repo
import stringEnDefaults from '../../../../adunit/src/strings/en';
import DataStore from '../base/plumbing/DataStore';
import C from '../C';
import Advert from '../base/data/Advert';
import { assert } from '../base/utils/assert';
import VariantControls from './VariantControls';
import SubCard from '../base/components/SubCard';
import ServerIO from '../plumbing/ServerIO';


function AdDesignExtraControls({ advert, path }) {
	assert(path, 'AdDesignExtraControls');
	return (<div>
		<CustomControls path={path} advert={advert} />

		<Card title="Advanced Documentation">
			<ul>
				<li><LinkOut href="https://docs.google.com/document/d/1HrhdtKRRmC-l_863ofu3HWnAyPHnD7UITi8s6IJlkvU">Notes on image and video optimisation</LinkOut></li>
				<li><LinkOut href="https://docs.google.com/spreadsheets/d/1QXY2WsGwSm-MRj01WsmrsdV_RH-_sagSkpnYJcVbLN8">Handy CSS examples</LinkOut></li>
			</ul>
		</Card>

		<VariantControls path={path} entity={advert} />

		<LayoutControls path={[...path, 'advanced']} advert={advert} />

		<AdvancedAdControls path={path} advert={advert} />

		<Card title="Custom text">
			<AdUnitTextEditor path={path} />
		</Card>

		<BannerControls entity={advert} />

		<Card title="External Tracking Urls">
			<TrackingUrls advert={advert} />
		</Card>

		<Card title="Languages" >
			<PropControl label="Language" path={path.concat("advanced")} prop="language" type="select" options={['en', 'fr', 'es', 'de', 'it', 'nl']} dflt="en" labels={['English', 'French', 'Spanish', 'German', 'Italian', 'Dutch']}/>
		</Card>

		<Card title="Mock Screenshots">
			<PropControl label="Landscape screenshot" path={path.concat('mock4size')} prop="landscape" type="imgUpload"
				help="Upload a screenshot of the adunit to use as a placeholder where many adunits will be shown - like the carousels on impact pages - to reduce bandwidth and CPU usage."
			/>
		</Card>
	</div>);
}


/** Is this custom string valid? Warn the user if it's very long (not counting HTML tag internals) */
const ctValid = s => s && s.replace(/<.*?>/g, '').length >= 60 ? 'Your custom text may be too long' : null;

/** Prop keys + names for the legacy custom-text strings */
const legacyStrings = { lockedMsg: 'Countdown (legacy)', readyMsg: 'Donation prompt (legacy)', completeMsg: 'Donation complete (legacy)' };

/** Macro keys to descriptions */
const macroDescs = {
	's,ss,mmss,sroll,ssroll,mmssroll': 'Time until donation unlocks. "s" for "2", "ss" for "02", "mmss" for "00:02", and add "roll" to add a rollover animation when the number changes.',
	seconds: 'The word "second(s)", pluralised appropriately for the number remaining',
	adsecs: 'The total time this advert takes to unlock',

	'charity,charityName': 'The name of the charity the user has chosen',
	'charityUrl,charityLogo,charityLogoWhite': 'The web address, logo, or white-silhouette logo of the chosen charity',
	'sponsor,advertiserName': 'The name of the sponsor',
	'advertiserUrl,advertiserLogo,advertiserLogoWhite': 'The web address, logo, or white-silhouette logo of the sponsor',

	thisDonation: 'The donation from this view, e.g. "£0.07"',
	ct: 'The total donated by this campaign, including this view, e.g. "£12,345.67"',
	totalPledged: 'The overall total the sponsor has pledged to charities (for campaigns where we can\'t do realtime donation counts)',
	'fractionDone,fractionLeft,percentDone,percentLeft': 'For custom HTML - how close/far from minview is the user, as a decimal from "0" to "1" or percentage from "0" to "100"',
	playing: 'For custom HTML - "playing" if the video is playing, prints nothing otherwise.',
	myGlLink: 'A link to My Good-Loop with UTM campaign tracking inserted and text "my.good-loop.com"'
};

/**
 * Turn the macro-to-definition list into a nice readable block
 * This is static content, so it's a rendered element and not a function
 * We use longhand Fragment below so the elements can have keys & silence React keyless-array warnings
 */
const Macros = Object.entries(macroDescs).map(([keys, desc]) => {
	// &#123; and &#125; are { and }
	const keyList = keys.split(',').map(key => <Fragment key={key}>&#123;{key}&#125; </Fragment>);

	return <div key={keys} className="mb-2"><strong className="ml-2">{keyList}</strong><br />{desc}</div>;
});


// TODO use adunit/src/strings/en.js to show the default string
const stringDescsDesignAd = {
	chooserLockedPrefix: 'Countdown in progress: "Free donation unlocked in (clock)"',
	chooserReady: 'Countdown complete: "Free donation unlocked! Pick a charity"',
	chooserComplete: 'Charity chosen: "Thank you!"',
	skip: '"Skip" button'
};
const stringDescsEndCard = {
	endCardChooserPrompt: 'End card: "Choose a charity" prompt',
	endCardDonationMade: 'End card: "Thank you! Donation made"',
	endCardSupporting: 'End card: "(brand) supporting (charity)"',
	endCardTotalPledge: 'End card: "Together, we\'re raising £(amount)"',
	donationInfo: 'End card: "Donation Info" button',
	close: 'End card: "Close" button',
};
/** all of them */
const stringDescs = {
	...stringDescsDesignAd,
	...stringDescsEndCard,
	socialDonationUnlocked: 'Social: "Donation Unlocked"',
	socialChooseCharity: 'Social: "Choose a charity"',
	socialTotalPledged: 'Social (no fixed cost per view): "Together, we\'re raising £(amount)"',
	socialTotalRunning: 'Social (set cost per view): "We\'ve raised £(amount) so far"',
	socialThankYou: 'Social (after pick)',
	socialDonationSent: 'Social (after pick)',

	yes: 'Generic "Yes"',
	no: 'Generic "No"',
	consentOK: 'CTA: "Opt In"',
	consentGiven: 'CTA: "Thanks for giving consent, manage preferences on my.good-loop.com"',
	poweredBy: '"Powered by" (appears above Good-Loop logo)',

	treeCounterPrefix: 'TADG: "Trees planted by these ads:..."',
	treeCounterPostfix: 'TADG: "...trees planted by these ads"',
	treesFunded: 'TADG: "Funded by:" above brand logo',
	treesTagline: 'TADG: The big "THIS AD / PLANTS / TREES" tagline blocks (use <br> for line breaks)',
	treesFindOutMore: 'TADG: The "Find out more" link below the tagline',
	tadgUnitBefore: 'TADG: Impact units to left of counter, eg £12345',
	tadgUnitAfter: 'TADG: Impact units to right of counter, eg 12345m²',
	treesEndcardTagline: 'TADG end card: THANK YOU / DONATION MADE (use <br> for line breaks)',
};


/**
 * Various string-override components - the generic "override by SID" one + several legacy fields
 * @param {{
 * 	path: {String[]} Path for the advert object
 * }}
 */
export function AdUnitTextEditor({ path, workflow }) {
	const lPath = path.concat('portalDonationMessageStrings');
	const ctPath = path.concat('customtext');

	const customInUse = DataStore.getValue(ctPath) || {};

	const [showSids, setShowSids] = useState(false);

	const showSidsButton = (
		<Button onClick={() => setShowSids(!showSids)}>{showSids ? 'Hide' : 'Show All'}</Button>
	);

	// add buttons to insert any available custom strings not already in use
	// ...Which strings to offer? default to all of them
	const sds = { DesignAd: stringDescsDesignAd, DesignPostEngagement: stringDescsEndCard }[workflow] || stringDescs;

	return <>
		{/* Only display the legacy string fields in advanced controls */}
		{/* HACK Restoring the legacy string overrides until the "advanced" editor gets the improvements I meant to put in. */}
		{/* TODO Do that and remove the "false &&" wedge I stuck in here*/}
		{!workflow && Object.entries(legacyStrings).map(([key, label]) => {
			const thisPath = lPath.concat(key);
			if (false && !DataStore.getValue(thisPath)) return '';
			return <PropControl key={key} label={label} path={lPath} prop={key} type="textarea" help="" validator={ctValid} />;
		})}

		<PropControl label="Custom text" type="entryset" path={path} prop="customtext" keyName="String ID" valueName="New text"
			help="You can override most of the adunit text here - see below for a guide."
			keywords="custom text"
		/>

		<p>Other overrides available: {showSidsButton}</p>
		{showSids && <table className="table table-hover"><tbody>
			{Object.entries(sds).map(([sid, desc]) => {
				const disabled = typeof customInUse[sid] === 'string';
				let addFn = () => DataStore.setValue(ctPath, { ...customInUse, [sid]: '' });
				let enDefault = stringEnDefaults[sid];
				return <tr key={sid}>
					<td><Button size="xs" disabled={disabled} onClick={addFn}><Misc.Icon fa="plus" /></Button></td>
					<td className="px-2 text-center"><code>{sid}</code></td>
					<td className="px-2"><small>{desc}</small></td>
					<td className="px-2"><small>{enDefault}</small></td>
				</tr>;
			})}
		</tbody></table>}
		{showSids && workflow && <p>There are more text snippets which can be edited - See the Advanced Controls for those</p>}

		<MacroHelp />
	</>;
}

export function MacroHelp() {
	const [showMacros, setShowMacros] = useState(false);
	return (<div className="explanation">
		Macros available: <Button onClick={() => setShowMacros(!showMacros)}>{showMacros ? 'Hide' : 'Show Macros'}</Button>
		{showMacros && <><p>Certain pieces of text, such as the name of the charity chosen by the user, need to be inserted dynamically by the adunit. You can show the adunit where to place this information by inserting macros in to your custom donation text. Macros are short strings, like &#123;s&#125;. A complete list of available macros, and the information that they will be replaced by, is given below.</p>
			<p>Example: Entering <code>Your donation will unlock in &#123;s&#125; &#123;seconds&#125;</code> will become &quot;Your donation will unlock in 15 seconds&quot; when viewing the actual advert.</p>
		</>
		}
		{showMacros && Macros}
	</div>);
}


/**
 * AB experiment controls (TODO and show results here too for convenience)
 * HACKS: removeFn and filterFn here are shims for the fact that we don't have key deletion on map/object elements server-side
 */
function ABWidget({ path, advert }) {
	return (
		<BSCard body className="mb-2">
			<PropControl keywords="AB A/B experiment random" type="keyvalue" path={path} prop="ab" label="A/B Experiments"
				$KeyProp={<PropControl label="&quot;B&quot; advert-id" />}
				$ValProp={<PropControl label="Share of impressions" type="number" min={0} max={1} step="0.01" help="[0-1] proportion" />}
				removeFn={(map, key) => ({ ...map, [key]: 0 })}
				filterFn={(k, v) => v !== 0}
			/>
			<div><small>A/B variants MUST use the same pricing and budget. Otherwise expect odd behaviour in reports.</small></div>
		</BSCard>
	);
}


/** Image label for layout/variant control */
function VariantLabel({ variant }) {
	return <>
		<img className="img-thumbnail" alt={`Layout: ${variant}`} src={`/img/${variant}.svg`} />
		<div>{variant}</div>
	</>;
}


/** Output extra layout controls specific to either social or WTD adverts */
function LayoutControls({ path, advert, ...rest }) {
	assert(path, "LayoutControls no path");
	return (
		<Card title="Layout">
			{advert && advert.format === 'social' ?
				<LayoutControlsSocial path={path} advert={advert} {...rest} />
				: <LayoutControlsWTD path={path} advert={advert} {...rest} />
			}
		</Card>
	);
}


/** Select alternate social splash video overlays & add custom CSS */
function LayoutControlsSocial({ path, advert }) {
	return <>
		<PropControl label="Splash Video Overlay" type="radio" prop="splashOverlayType" path={path} className="player-variants"
			help="Select an overlay type for the mock splash video. The default is &quot;wave-bottom&quot;"
			options={['wave-bottom', 'wave-top', 'nowave-bottom', 'nowave-top']}
			labels={(variant) => <VariantLabel variant={variant} />}
		/>
		<PropControl type="code" popup label="Splash Custom CSS" prop="splashCSS" path={path} lang="css"
			help="CSS to apply to the mock social splash video on the demo page"
		/>
	</>;
}


/** Variant selector and variant-specific controls (currently only TAPT) */
function LayoutControlsWTD({ path, advert }) {
	return <>
		<PropControl label="Player Design Type" type="radio" prop="playerVariant" path={path} className="player-variants"
			help="Select an alternate layout to use. Landscape slots will default to &quot;landscape-b&quot;."
			options={['landscape-a', 'landscape-b', 'landscape-tall', 'trees']}
			labels={(variant) => <VariantLabel variant={variant} />}
		/>

		<TAPTExtraControls advert={advert} path={path} />
	</>;
}


/** Controls for setting up a single fg/bg layer in the This Ad Does Good format. */
function ParallaxLayerControl({path, delFn}) {
	return <SubCard><Row>
		<Col sm="5">
			<PropControl type="imgUpload" path={path} prop="img" label="Layer image" />
		</Col>
		<Col sm="5">
			<PropControl type="imgUpload" path={path} prop="mask" label="Layer mask" />
		</Col>
		<Col sm="2">
			<PropControl type="number" path={path} prop="shift" label="Parallax shift %" />
			<Button className="pull-right" color="danger" onClick={delFn}>Delete Layer</Button>
		</Col>
	</Row></SubCard>;
}


/** Presents a list of layer controls for the foreground or background. */
function ParallaxLayersControl({path, fg}) {
	const layerList = DataStore.getValue(path) || [];
	const listType = fg ? 'foreground' : 'background';

	const addLayer = () => DataStore.setValue(path, [...layerList, {}]);
	const deleteLayer = index => {
		const newList = [...layerList];
		newList.splice(index, 1);
		DataStore.setValue(path, newList);
	};

	return <Container className="mb-4">
		{layerList && layerList.length ? (
			layerList.map((layer, i) => <ParallaxLayerControl key={`${listType}-${i}`} path={[...path, i]} delFn={() => deleteLayer(i)} />)
		) : <>No {listType} layers defined.</>}
		<Row>
			<Col sm="12">
				<Button color="primary" onClick={addLayer}>Add a new {listType} layer</Button>
			</Col>
		</Row>
	</Container>;
}


/** Controls specific to the "This Ad Plants Trees / Does Good" type unit. */
function TAPTExtraControls({advert, path}) {
	if (!isTAPT(advert)) return null;

	const [show, setShow] = useState(false);
	const [showHelp, setShowHelp] = useState(false);
	const toggle = () => setShow(!show);

	const help = showHelp ? <ul className="mt-2">
		<li>Layers further down this list appear in front of higher ones.</li>
		<li>Backgrounds will appear behind the video; foregrounds will be in front of it. All layers will still be behind the text and other interface elements.</li>
		<li>The "Parallax shift" control lets you animate layer motion. Layers with positive values will slide to the right during playback, negative will go left.</li>
		<li>Set progressively changing values as you go down the list (e.g. 0.5, 2, 4) for a simulated depth effect.</li>
		<li>You should have at least one background layer - everything else is optional.</li>
		<li>Your rearmost layer should have no transparency. Everything further forward MUST have transparency.</li>
		<li>For mocks and demos, just upload a PNG to the "Layer image" field and ignore "Layer mask".</li>
		<li>Before going live, for bandwidth reasons, you should split each layer into a JPG image with optimised PNG mask - talk to Roscoe about this.</li>
		<li>Delete all layers to revert to the default "This Ad Plants Trees" visual.</li>
	</ul> : null;

	const textPath = [...path.slice(0, -1), 'customtext'];

	return <>
		<Row>
			<Col sm="4">
				<PropControl label="Trees Per View" type="number" prop="treesPerView" path={path}
					help="For a dynamic &quot;Trees planted by this ad&quot; counter"
				/>
			</Col>
			<Col sm="4">
				<PropControl label="Trees Total" type="number" prop="treeCount" path={path}
					help="For a fixed &quot;Trees planted by this ad&quot; counter"
				/>
			</Col>
			<Col sm="4">
				<p>Create a custom "This Ad Does Good" advert</p>
				<Button onClick={toggle} size="lg">Customise</Button>
			</Col>
		</Row>
		<Modal size="xl" isOpen={show} toggle={toggle}>
			<ModalHeader toggle={toggle}>
				Customise "This Ad Does Good"
			</ModalHeader>
			<ModalBody>
				<h4>Text</h4>
				<Container><Row>
					<Col sm="6">
						<PropControl type="text" path={textPath} prop="treesTagline" label="Tagline" help="The main tagline. Separate lines with <br>." />
					</Col>
					<Col sm="6">
						<PropControl type="text" path={textPath} prop="treesFindOutMore" label="Link text" help="The link below the tagline." />
					</Col>
					<Col sm="6">
						<PropControl type="text" path={textPath} prop="treesFunded" label="Funded By" help="The &quot;Funded By&quot; text above the brand logo" />
					</Col>
					<Col sm="6">
						<PropControl type="text" path={textPath} prop="treesEndcardTagline" label="End card message" help="e.g. &quot;Thank You / Donation Made&quot; above counter on end card. Separate lines with <br>."/>
					</Col>
					<Col sm="6">
						<PropControl type="text" path={textPath} prop="treeCounterPrefix" label="Counter explanation (Above)" help="Things done: [new line] 12345" />
					</Col>
					<Col sm="6">
						<PropControl type="text" path={textPath} prop="treeCounterPostfix" label="Counter explanation (Below)" help="12345 [new line] Things done" />
					</Col>
					<Col sm="6">
						<PropControl type="text" path={textPath} prop="tadgUnitBefore" label="Unit before counter" help="Appears in a box to the left of the counter digits - for units like £" />
					</Col>
					<Col sm="6">
						<PropControl type="text" path={textPath} prop="tadgUnitAfter" label="Unit after counter" help="Appears in a box to the right of the counter digits - for units like m²" />
					</Col>
				</Row></Container>
				<h4>Graphics</h4>
				<Container className="mb-4">
					Upload images to use as the layered backgrounds and foregrounds in a "This Ad Does Good" type unit.
					(For an empty foreground or background - skipping the default forest image - create a layer and enter <code>none</code> in the "Layer image" field.)
					<Button color="primary" className="pull-right" onClick={() => setShowHelp(!showHelp)}>{showHelp ? 'Less' : 'More'} help</Button>
					{help}
				</Container>
				<h4>Background</h4>
				<ParallaxLayersControl path={[...path, 'parallax', 'bg']} />
				<h4>Foreground</h4>
				<ParallaxLayersControl path={[...path, 'parallax', 'fg']} fg />
			</ModalBody>
		</Modal>
	</>;
}


/**
 * HACK Is this a This-Ad-Plants-Trees ad?
 * @param {?Advert} advert 
 */
const isTAPT = advert => {
	if (!advert || !advert.advanced) {
		return false;
	}
	return /^trees/.test(advert.advanced?.playerVariant);
};


function LegacyBranchControl({path}) {
	// TODO: Update AdUnit/Portal build scripts to automatically:
	//   a) Create a directory in adunit/legacy-units/<branch name> with the previous release branch
	//   b) Add the name of that release branch to /data/legacy-adunit-branches.json
	const legacyBranches = DataStore.fetch(['misc', 'legacybranches'], () => {
		return ServerIO.load(`${ServerIO.PORTAL_ENDPOINT}/data/legacy-adunit-branches.json`, {});
	}).value;

	const extraProps = legacyBranches ? { type: 'select', options: legacyBranches } : { type: 'text'};

	return (
		<PropControl label="Custom unit branch" path={path} prop="legacyUnitBranch" options={legacyBranches} dflt=""
			help="Use a previous version of unit.js for this advert. Defaults to newest stable release. If empty it uses the unstable live version of adunit."
			{...extraProps}
		/>
	);
}


function AdvancedAdControls({ path, advert, ...stuff }) {
	let localStatus = DataStore.getLocalEditsStatus(getType(advert), advert.id) || C.STATUS.clean;
	let aka = DataStore.getValue(path.concat('aka'));
	// const appNexusUrl = `/appnexus/${getType(advert)}/${encURI(advert.id)}`;

	return (<Card title="Advanced" {...stuff}>
		<OnXControls path={path} />

		<ABWidget path={path} advert={advert} />

		<LegacyBranchControl path={path} />

		<PropControl label="Allow other charities?"
			help='Mostly the Advertiser will pick the charities. "Allow others" allows for one charity each picked by the publisher, the advertiser, and the user.'
			item={advert.charities} path={path.concat('charities')} prop="otherCharities" type="select"
			options={['ALLOW_OTHERS', 'MUST_USE_ONLY_MINE', 'MUST_USE_SOME_OF_MINE']}
			labels={{
				ALLOW_OTHERS: "allow other charities (recommended)",
				MUST_USE_ONLY_MINE: "must use only my charities",
				MUST_USE_SOME_OF_MINE: "must use one of my charities"
			}}
			dflt="MUST_USE_ONLY_MINE"
		/>
	</Card>);
}



function TrackingUrls({ advert }) {
	if (!advert || !advert.id) return null;
	Advert.assIsa(advert);

	const wpath = ['widget', 'Advert', 'TrackingUrls', advert.id];
	let setup = DataStore.getValue(wpath) || {};

	let charitiesList = Advert.charityList(advert);
	assert(charitiesList.map, "TrackingUrls - no charitiesList.map()?!", charitiesList);

	return (
		<div>
			Tracker urls for 3rd party versions of &quot;our&quot; adverts. Use with
			<Row>
				<Col md="4">
					<PropControl label="Macro format" path={wpath} prop="macroFormat" type="select"
						options={['', 'Google / DoubleClick', 'Loop.Me', 'AppNexus', 'Teads', 'Avocet', 'other']}
						help={setup.macroFormat === 'other' ? <>Use the <code>Misc</code> field for any other macros or tracking codes.</> : null}
					/>
				</Col>
				<Col md="4"><PropControl label="Agency" path={wpath} prop="agency" /></Col>
				<Col md="4"><PropControl label="Misc" path={wpath} prop="misc" help="Use this for misc macros" /></Col>
			</Row>

			{!setup.macroFormat ? <div className="alert alert-warning">No macro-format set = no site info</div> : null}
			<ul>
				<TrackingUrl advert={advert} name="Impressions" evt="render" setup={setup} />
				<TrackingUrl advert={advert} name="Advert is visible" evt="visible" setup={setup} />
				<TrackingUrl advert={advert} name="User engages and the video starts plaing" evt="open" setup={setup} />
				<TrackingUrl advert={advert} name="Video reaches 15 seconds (advertiser is charged)" evt="minview" setup={setup} />
				{charitiesList.map(c => <TrackingUrl key={c.id} advert={advert} name={'User picks charity: ' + c.id} evt="donation" cid={c.id} setup={setup} />)}
				<TrackingUrl advert={advert} name="Video reaches the end" evt="endvideo" setup={setup} />
				<TrackingUrl advert={advert} name="Watch time (replacing *elapsed-milliseconds*)"
					evt="elapse" setup={setup} extra="p.dt=*elapsed-milliseconds*"
				>
					<p>
						How much of the video they watch, so we we can report back to the client on average view-length. That could be per-second logging, or a single call when the video is closed / user navigated away.
					</p>
				</TrackingUrl>
			</ul>
		</div>
	);
}


// see https://wiki.appnexus.com/display/adnexusdocumentation/AppNexus+Macros
// AppNexus uses ${} as its macro marker - eslint reasonably thinks that's an error in a normal string
/* eslint-disable no-template-curly-in-string */
const appNexusMacros = {
	'p.site': '${PUBLISHER_ID}',
	'p.url': '${REFERER_URL_ENC}',
	'p.xbid': '${AUCTION_ID}',
	'p.width': '${WIDTH}',
	'p.height': '${HEIGHT}',
	'p.ip': '${USER_IP}',
	'p.uxid': '${USER_ID}%40appnexus',
	'p.city': '${USER_CITY}',
	'p.age': '${AGE}',
};
/* eslint-enable no-template-curly-in-string */

// ?? IP address??
const googleMacros = {
	'p.site': '%%SITE%%',
	'p.url': '%%PATTERN:url%%',
	'p.width': '%%WIDTH%%',
	'p.height': '%%HEIGHT%%',
	'p.adunit': '%%ADUNIT%%',
};

// ??any more stuff??
const teadsMacros = {
	'p.site': '[WNAME]',
	'p.ip': '[IP_ADDRESS]'
};

const avocetMacros = {
	'p.site': '{{.EXTERNAL_PUBLISHER_ID}}',
	'p.url': '{{.REQUEST_DOMAIN}}',
	cb: '{{.CACHEBUSTER}}',
	'p.device': '{{.DEVICE_TYPE_ID}}',
	'p.ip': '{{.IPV4}}',
	'p.cuid': '{{.COOKIE_UID}}',
	'p.uxid': '{{.UUID}}%40avocet',
	'p.muid': '{{.MOBILE_IFA}}',
	'p.xbid': '{{.INTERNAL_AUCTION_ID}}',
};

const queryBit = obj => Object.entries(obj).map(([key, val]) => `${key}=${val}`).join('&');

const macroFormats = {
	appnexus: queryBit(appNexusMacros),
	google: queryBit(googleMacros),
	teads: queryBit(teadsMacros),
	avocet: queryBit(avocetMacros),
	// Nope - nothing for vpaid on loop.me :(
	// 'loop.me': queryBit({'p.site': '{publisher_id}_{publisher_name}'})
};

/**
 * ??
 * @param {*} param0 
 */
function TrackingUrl({ name, evt, setup, cid, advert, extra, children }) {
	let umacros = "";
	const macroFormat = (setup.macroFormat || "").toLowerCase();
	// if (mf === 'loop.me') macros = "p.site={publisher_id}_{publisher_name}"; 

	Object.entries(macroFormats).forEach(([mf, query]) => {
		if (macroFormat.indexOf(mf) >= 0) umacros = query;
	});

	let via = setup.agency || macroFormat;
	// NB: macros should not be escaped
	let turl = 'https://lg.good-loop.com/lg?' + [
		't=' + evt,
		'd=gl',
		via ? 'p.via=' + escape(via) : null,
		'p.vert=' + escape(advert.id),
		'p.campaign=' + escape(advert.campaign),
		'p.env=external',
		cid ? 'p.cid=' + escape(cid) : null,
		extra,
		umacros,
		setup.misc
	].filter(x => x).join('&');

	return <li key={evt}>{name}: <small><code>{turl}</code></small>{children}</li>;
}

/** Card with most commonly-used controls for  */
function CustomControls({ advert, path }) {
	const advPath = [...path, 'advanced'];

	return (
		<Card title="Customisation">
			<Row>
				<Col sm="12" md="12" lg="12" id="ad-custom-css-textarea" >
					<PropControl type="code" popup label="Custom CSS" lang="css" prop="customcss" path={advPath}
						help="Extra CSS rules, specific to this advert (generally for the lightbox), which will be appended to the DOM."
					/>
					<br/><br/>
				</Col>
				<Col sm="6" md="4" lg="4">
					<PropControl label="Custom .js" type="textarea" prop="customjs" path={advPath}
						help="Extra JS code, specific to this advert (generally for special effects). Use rarely and wisely."
					/>
				</Col>
				<Col sm="6" md="4">
					Custom fonts:<br />
					Go to the <LinkOut href="https://calstat.good-loop.com/#fonts">management page</LinkOut> to upload fonts and generate CSS.
				</Col>
			</Row>
			<PropControl label="Custom Impact Hub link" type="url" prop="impactHubUrl" path={advPath}
				help="Send the user to a different link for the Impact Hub instead of the one for this campaign."
			/>
		</Card>
	);
}



export default AdDesignExtraControls;
export { LayoutControls, ParallaxLayersControl };
